import { differenceInDays } from 'date-fns';
import { getTooltip } from '@/helpers/tooltips';
import { parseDate } from '@/helpers/date';
import { getFederalAlerts } from '@/components/library/flsaSurveys/helpers';

export const FLSA_ALERT_PREVIEW_LABELS = {
  no_flsa_submission: 'No FLSA submission',
  review_overdue: 'Review overdue',
  status_mismatch: 'Mismatched status',
  outdated_form: 'Outdated form',
  profile_change: 'Profile changed',
};

export const FLSA_ALERT_DISPLAY_ORDER = [
  'no_flsa_submission',
  'review_overdue',
  'status_mismatch',
  'outdated_form',
  'profile_change',
];

export const FLSA_ALERT_PREVIEW_THEMES = {
  no_flsa_submission: {
    color: 'red',
    icon: 'mdi-minus-box-outline',
  },
  review_overdue: {
    color: 'yellow',
    icon: 'mdi-timer-sand-complete',
  },
  status_mismatch: {
    color: 'pink',
    icon: 'mdi-sync-alert',
  },
  outdated_form: {
    color: 'purple',
    icon: 'mdi-calendar-alert',
  },
  profile_change: {
    color: 'navy',
    icon: 'mdi-file-exchange-outline',
  },
};

const FLSA_ALERT_PREVIEW_THEME_FALLBACK = {
  color: 'grey',
  icon: 'mdi-alert-circle-outline',
};

export const FLSA_ALERT_LEARN_MORE_ARTICLE_ID = '699784510f8fc200dfd696ac';

export const FLSA_ALERT_LEARN_MORE_URL = `https://help.datapeople.io/article/${FLSA_ALERT_LEARN_MORE_ARTICLE_ID}`;

export const getFederalAlertCodes = (item) => [...getFederalAlerts(item)];

export const sortFlsaAlertCodes = (codes) => [...(codes || [])].sort((codeA, codeB) => {
  const orderA = FLSA_ALERT_DISPLAY_ORDER.indexOf(codeA);
  const orderB = FLSA_ALERT_DISPLAY_ORDER.indexOf(codeB);

  return (orderA === -1 ? FLSA_ALERT_DISPLAY_ORDER.length : orderA)
    - (orderB === -1 ? FLSA_ALERT_DISPLAY_ORDER.length : orderB);
});

export const alertShowsCreateActions = (code) => code !== 'status_mismatch';

export const getFlsaAlertPreviewTheme = (code) => (
  FLSA_ALERT_PREVIEW_THEMES[code] || FLSA_ALERT_PREVIEW_THEME_FALLBACK
);

export const getFlsaAlertPreviewLabel = (code) => (
  FLSA_ALERT_PREVIEW_LABELS[code] || ''
);

const getDaysSinceSubmission = (latestSubmission) => {
  if (!latestSubmission?.created_at) return null;

  const createdAt = parseDate(latestSubmission.created_at);

  if (!createdAt) return null;

  return differenceInDays(new Date(), createdAt);
};

export const getFlsaAlertPreviewLearnMoreHref = (code) => {
  if (code !== 'review_overdue') return '';

  const { html } = getTooltip('flsa-report-alert-out-of-sla');
  const match = html?.match(/href="([^"]+)"/);

  return match?.[1] || FLSA_ALERT_LEARN_MORE_URL;
};

export const getFlsaAlertPreviewBody = ({ code, latestSubmission } = {}) => {
  switch (code) {
    case 'no_flsa_submission':
      return 'No Federal FLSA questionnaire has been completed for this job profile. Create one to document the exemption status for this role.';
    case 'review_overdue': {
      const days = getDaysSinceSubmission(latestSubmission);
      const daysText = days !== null ? `${days}` : '[X]';

      return `The Federal FLSA questionnaire for this job profile was last completed ${daysText} days ago, which exceeds your organization's review period. A new questionnaire is required.`;
    }
    case 'status_mismatch':
      return 'The exemption status recorded on the Federal FLSA questionnaire does not match what is listed in your HRIS. Review both records and update whichever is incorrect.';
    case 'outdated_form':
      return 'The Federal FLSA questionnaire completed for this job profile is based on an outdated version of the survey. A new questionnaire is required to reflect current regulatory requirements.';
    case 'profile_change':
      return '';
    default:
      return '';
  }
};
